// ---------------------------------------------------------------------------
// F3b C3: máquina de estados do modo histórico (puro, testado). O estado de verdade fica espalhado no App
// (interactionMode, isReturningToLive, roleta aberta, troca em andamento, gesto ativo); o App aplica AQUI os eventos
// que mudam o modo e copia o resultado para esses estados. Os testes percorrem todos os estados alcançáveis.
//
// Estados (nomes do relatório):
//   presente/órbita  : mode 'orbit', sem autoplay (a vista pode estar parada no passado: "passado parado")
//   histórico armado : mode 'time' (MÁQUINA DO TEMPO ARMADA), sem dedo
//   arrastando       : mode 'time' + dedo (gesto 'time_drag')
//   autoplay         : volta ao presente (15/60/180)
//   roleta aberta    : menu radial na tela
//   troca            : série nova carregando (a anterior na tela)
//
// Regras (F3b, decisões 3 e 4 do Mateus):
//   - abrir a roleta FECHA a navegação histórica (e o autoplay) e deixa o gráfico onde está; a troca de timeframe
//     não depende de estar no modo histórico;
//   - a troca de série também fecha a navegação (o momento do centro é levado para a série nova);
//   - X: sai do modo histórico e deixa o gráfico onde está (não volta ao presente);
//   - armar ou arrastar não acontece com a roleta aberta ou com troca em andamento;
//   - o autoplay (volta ao presente) pode ser pedido durante a troca: vale depois que a série nova é posicionada.
// Invariantes: arrastando ⇒ histórico; histórico ⇒ sem autoplay, sem roleta e sem troca ("histórico ativo, mas preso
// no ao vivo" era histórico + roleta/troca); autoplay ⇒ sem roleta.
// ---------------------------------------------------------------------------

export type HistMode = 'orbit' | 'time' | 'crosshair' | 'zone';
export interface HistState { mode: HistMode; returning: boolean; dragging: boolean; roulette: boolean; switching: boolean }
export type HistEvent =
  | 'armar' | 'arrastar' | 'soltar' | 'ocioso'
  | 'autoplay' | 'autoplayFim' | 'congelar'
  | 'x' | 'abrirRoleta' | 'fecharRoleta' | 'escolherTimeframe'
  | 'trocaInicio' | 'trocaFim' | 'trocaFalha';
export const HIST_EVENTS: readonly HistEvent[] = ['armar', 'arrastar', 'soltar', 'ocioso', 'autoplay', 'autoplayFim', 'congelar', 'x',
  'abrirRoleta', 'fecharRoleta', 'escolherTimeframe', 'trocaInicio', 'trocaFim', 'trocaFalha'];
export const HIST_INITIAL: HistState = { mode: 'orbit', returning: false, dragging: false, roulette: false, switching: false };

/** Sai da navegação histórica sem mexer na posição (o App congela o alvo na posição se havia autoplay). */
const leaveNav = (s: HistState): HistState => ({ ...s, mode: s.mode === 'time' ? 'orbit' : s.mode, returning: false, dragging: false });

export function histStep(s: HistState, e: HistEvent): HistState {
  switch (e) {
    case 'armar': return s.roulette || s.switching ? s : { ...s, mode: 'time', returning: false };
    case 'arrastar': return s.mode === 'time' && !s.returning && !s.roulette && !s.switching ? { ...s, dragging: true } : s;
    case 'soltar': return s.dragging ? { ...s, dragging: false } : s;
    case 'ocioso': return s.mode === 'time' && !s.dragging ? { ...s, mode: 'orbit' } : s;
    case 'autoplay': return s.roulette ? s : { ...s, mode: 'orbit', returning: true, dragging: false };
    case 'autoplayFim': return { ...s, returning: false };
    case 'congelar': return leaveNav(s);
    case 'x': return leaveNav(s);
    case 'abrirRoleta': return { ...leaveNav(s), roulette: true };
    case 'fecharRoleta': return { ...s, roulette: false };
    case 'escolherTimeframe': return { ...leaveNav(s), roulette: false, switching: true };
    case 'trocaInicio': return { ...leaveNav(s), switching: true };
    case 'trocaFim': return { ...s, switching: false };
    case 'trocaFalha': return s;                             // a série anterior fica; "tentar de novo" (falha ≠ fim)
  }
}

/** Nome do estado (relatório e diagnóstico). */
export function histName(s: HistState): string {
  if (s.switching) return 'troca';
  if (s.roulette) return 'roleta aberta';
  if (s.returning) return 'autoplay';
  if (s.mode === 'time') return s.dragging ? 'arrastando' : 'histórico armado';
  return 'presente/órbita';
}

/** Invariantes violadas (vazio = estado válido). */
export function histViolations(s: HistState): string[] {
  const v: string[] = [];
  if (s.dragging && s.mode !== 'time') v.push('arrastando fora do modo histórico');
  if (s.mode === 'time' && s.returning) v.push('histórico e autoplay juntos');
  if (s.mode === 'time' && s.roulette) v.push('histórico ativo com a roleta aberta (preso)');
  if (s.mode === 'time' && s.switching) v.push('histórico ativo durante a troca de série (preso no ao vivo)');
  if (s.returning && s.roulette) v.push('autoplay com a roleta aberta');
  return v;
}

/** Estados alcançáveis a partir de `start` aplicando `step` (busca em largura). */
export function histReachable(step: (s: HistState, e: HistEvent) => HistState = histStep, start: HistState = HIST_INITIAL): HistState[] {
  const key = (s: HistState) => `${s.mode}|${+s.returning}|${+s.dragging}|${+s.roulette}|${+s.switching}`;
  const seen = new Map<string, HistState>([[key(start), start]]), queue = [start];
  while (queue.length) { const s = queue.shift()!; for (const e of HIST_EVENTS) { const n = step(s, e); const k = key(n); if (!seen.has(k)) { seen.set(k, n); queue.push(n); } } }
  return [...seen.values()];
}

/** Bandeira de execução lida pelo árbitro de gestos da cena (fora do React): troca de série em andamento. */
export const histRuntime = { switching: false };
